import { useState, useEffect } from 'react';
import logger from '../../lib/logger';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { participantesApi, eventosApi, lotesApi } from '../../lib/api';
import { EditParticipanteDialog } from '../../components/admin/EditParticipanteDialog';
import { Users, Search, Eye, Mail, Phone, MapPin, Ticket, Download, FileSpreadsheet, FileText, CheckCircle, Clock, UserPlus, Tag, Trash2, Edit, Send, Printer, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ParticipantesPage = () => {
  const [participantes, setParticipantes] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedParticipante, setSelectedParticipante] = useState(null);
  const [filterEvento, setFilterEvento] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLote, setFilterLote] = useState('all');
  const [filterCheckin, setFilterCheckin] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [participanteToDelete, setParticipanteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [participanteToEdit, setParticipanteToEdit] = useState(null);

  const openEdit = (participante) => {
    setParticipanteToEdit(participante);
    setEditOpen(true);
  };

  const handleEdited = (updated) => {
    setParticipantes(prev => prev.map(p => ((p.participante_id || p.id) === (updated.participante_id || updated.id) ? { ...p, ...updated } : p)));
    if (selectedParticipante && (selectedParticipante.participante_id || selectedParticipante.id) === (updated.participante_id || updated.id)) {
      setSelectedParticipante(prev => ({ ...prev, ...updated }));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [participantesRes, eventosRes, lotesRes] = await Promise.all([
        participantesApi.list(),
        eventosApi.list(),
        lotesApi.list(),
      ]);
      setParticipantes(participantesRes.data);
      setEventos(eventosRes.data);
      setLotes(lotesRes.data);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (participante) => {
    setSelectedParticipante(participante);
    setDetailsOpen(true);
  };

  const openDeleteDialog = (participante) => {
    setParticipanteToDelete(participante);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!participanteToDelete) return;
    
    setDeleting(true);
    try {
      const participanteId = participanteToDelete.participante_id || participanteToDelete.id;
      await participantesApi.delete(participanteId);
      
      toast.success('Participante excluído com sucesso');
      setDeleteDialogOpen(false);
      setDetailsOpen(false);
      setParticipanteToDelete(null);
      
      // Refresh data
      fetchData();
    } catch (error) {
      logger.error('Error deleting participante:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Erro ao excluir participante';
      toast.error(msg, { duration: 8000 });
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateStatus = async (participanteId, statusType, newStatus) => {
    setUpdatingStatus(true);
    try {
      const data = {};
      if (statusType === 'pagamento') {
        data.status_pagamento = newStatus;
      } else if (statusType === 'checkin') {
        data.status_checkin = newStatus;
      }
      
      await participantesApi.updateStatus(participanteId, data);
      
      toast.success('Status atualizado com sucesso');
      
      // Update local state
      if (selectedParticipante && (selectedParticipante.participante_id === participanteId || selectedParticipante.id === participanteId)) {
        setSelectedParticipante(prev => ({
          ...prev,
          ...(statusType === 'pagamento' ? { status_pagamento: newStatus } : { status_checkin: newStatus })
        }));
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      logger.error('Error updating status:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao atualizar status';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleResendEmail = async () => {
    if (!selectedParticipante) return;
    const participanteId = selectedParticipante.participante_id || selectedParticipante.id;
    setResendingEmail(true);
    try {
      const res = await participantesApi.resendEmail(participanteId);
      toast.success(`E-mail enviado para ${res.data?.destinatario || selectedParticipante.email}`);
      fetchData();
    } catch (error) {
      logger.error('Error resending email:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Erro ao reenviar e-mail';
      toast.error(msg, { duration: 10000, description: 'Verifique a configuração da Resend.' });
    } finally {
      setResendingEmail(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d 'de' MMM 'de' yyyy, HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getEventoName = (eventoId) => {
    const evento = eventos.find(e => e.id === eventoId);
    return evento?.nome_evento || 'Evento não encontrado';
  };

  const getLoteName = (loteId) => {
    const lote = lotes.find(l => l.id === loteId);
    return lote?.nome_lote || 'Lote não encontrado';
  };

  const getExportParams = () => {
    const params = {};
    if (filterStatus !== 'all') params.status_pagamento = filterStatus;
    if (filterLote !== 'all') params.lote_id = filterLote;
    return params;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = getExportParams();
      const url = participantesApi.exportCsv(params);
      
      // Open download with authentication
      window.open(url, '_blank');
      
      toast.success('Exportação CSV iniciada');
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Erro ao exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendente: 'badge-warning',
      aprovado: 'badge-success',
      cortesia: 'bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full text-xs font-medium',
      cancelado: 'badge-error',
    };
    const labels = {
      pendente: 'Pendente',
      aprovado: 'Aprovado',
      cortesia: 'Cortesia',
      cancelado: 'Cancelado',
    };
    return <span className={styles[status] || 'badge-warning'}>{labels[status] || status}</span>;
  };

  const getOrigemBadge = (origem) => {
    const styles = {
      admin_manual: 'bg-blue-500/20 text-blue-400',
      site: 'bg-gray-500/20 text-gray-400',
      mercado_pago: 'bg-green-500/20 text-green-400',
      teste: 'bg-yellow-500/20 text-yellow-400',
    };
    const labels = {
      admin_manual: 'MANUAL',
      site: 'SITE',
      mercado_pago: 'MP',
      teste: 'TESTE',
    };
    const origemValue = origem || 'site';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[origemValue] || styles.site}`}>
        <Tag className="w-3 h-3" />
        {labels[origemValue] || origemValue.toUpperCase()}
      </span>
    );
  };

  const getCheckinBadge = (checkinStatus) => {
    if (checkinStatus === 'presente') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 text-xs">
          <CheckCircle className="w-3 h-3" />
          Presente
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 text-xs">
        <Clock className="w-3 h-3" />
        Não chegou
      </span>
    );
  };

  const filteredParticipantes = participantes.filter(p => {
    const matchesEvento = filterEvento === 'all' || p.evento_id === filterEvento;
    const matchesStatus = filterStatus === 'all' || p.status_pagamento === filterStatus;
    const matchesLote = filterLote === 'all' || p.lote_id === filterLote;
    const matchesCheckin = filterCheckin === 'all' || 
      (filterCheckin === 'presente' && p.checkin_status === 'presente') ||
      (filterCheckin === 'nao_chegou' && (p.checkin_status === 'nao_chegou' || !p.checkin_status));
    const matchesSearch = searchTerm === '' || 
      p.nome_responsavel.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.nome_conjuge.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesEvento && matchesStatus && matchesLote && matchesCheckin && matchesSearch;
  });

  const filteredLotes = filterEvento === 'all' 
    ? lotes 
    : lotes.filter(l => l.evento_id === filterEvento);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="participantes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Participantes</h1>
            <p className="text-gray-400">Gerencie os participantes inscritos</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/participantes/cadastro-manual">
              <Button
                className="bg-kairos-orange hover:bg-kairos-orange/90 text-white"
                data-testid="cadastro-manual-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Cadastro Manual
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => {
                const params = new URLSearchParams();
                if (filterStatus !== 'all') params.set('status', filterStatus);
                if (filterEvento !== 'all') params.set('evento', filterEvento);
                if (filterLote !== 'all') params.set('lote', filterLote);
                if (filterCheckin !== 'all') params.set('checkin', filterCheckin);
                const url = `/admin/participantes/imprimir${params.toString() ? '?' + params.toString() : ''}`;
                window.open(url, '_blank');
              }}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
              data-testid="print-list-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir Lista
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport()}
              disabled={exporting || filteredParticipantes.length === 0}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
              data-testid="export-csv-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {exporting ? 'Exportando...' : 'Exportar CSV'}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-obsidian-50 border-white/10 text-white"
              data-testid="search-input"
            />
          </div>
          <Select value={filterEvento} onValueChange={(val) => { setFilterEvento(val); setFilterLote('all'); }}>
            <SelectTrigger className="w-full md:w-[200px] bg-obsidian-50 border-white/10 text-white">
              <SelectValue placeholder="Filtrar por evento" />
            </SelectTrigger>
            <SelectContent className="bg-obsidian-200 border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">Todos os eventos</SelectItem>
              {eventos.map(evento => (
                <SelectItem key={evento.id} value={evento.id} className="text-white hover:bg-white/10">
                  {evento.nome_evento}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterLote} onValueChange={setFilterLote}>
            <SelectTrigger className="w-full md:w-[180px] bg-obsidian-50 border-white/10 text-white">
              <SelectValue placeholder="Filtrar por lote" />
            </SelectTrigger>
            <SelectContent className="bg-obsidian-200 border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">Todos os lotes</SelectItem>
              {filteredLotes.map(lote => (
                <SelectItem key={lote.id} value={lote.id} className="text-white hover:bg-white/10">
                  {lote.nome_lote}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-[180px] bg-obsidian-50 border-white/10 text-white">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent className="bg-obsidian-200 border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">Todos os status</SelectItem>
              <SelectItem value="pendente" className="text-white hover:bg-white/10">Pendente</SelectItem>
              <SelectItem value="aprovado" className="text-white hover:bg-white/10">Aprovado</SelectItem>
              <SelectItem value="cancelado" className="text-white hover:bg-white/10">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCheckin} onValueChange={setFilterCheckin}>
            <SelectTrigger className="w-full md:w-[160px] bg-obsidian-50 border-white/10 text-white">
              <SelectValue placeholder="Check-in" />
            </SelectTrigger>
            <SelectContent className="bg-obsidian-200 border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">Todos</SelectItem>
              <SelectItem value="presente" className="text-white hover:bg-white/10">Presentes</SelectItem>
              <SelectItem value="nao_chegou" className="text-white hover:bg-white/10">Não chegaram</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total</p>
                <p className="text-2xl font-bold text-white">{filteredParticipantes.length}</p>
              </div>
              <Users className="w-8 h-8 text-gray-600" />
            </CardContent>
          </Card>
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Confirmados</p>
                <p className="text-2xl font-bold text-green-500">
                  {filteredParticipantes.filter(p => p.status_pagamento === 'aprovado').length}
                </p>
              </div>
              <Ticket className="w-8 h-8 text-green-500/30" />
            </CardContent>
          </Card>
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-500">
                  {filteredParticipantes.filter(p => p.status_pagamento === 'pendente').length}
                </p>
              </div>
              <Users className="w-8 h-8 text-yellow-500/30" />
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        {filteredParticipantes.length === 0 ? (
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Nenhum participante encontrado.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-0 overflow-x-auto">
              <table className="table-dark">
                <thead>
                  <tr>
                    <th>Casal</th>
                    <th>E-mail</th>
                    <th>Telefone</th>
                    <th>Cidade</th>
                    <th>Origem</th>
                    <th>Status</th>
                    <th>Check-in</th>
                    <th>Código</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipantes.map((participante) => (
                    <tr key={participante.id} data-testid={`participante-row-${participante.id}`}>
                      <td>
                        <div>
                          <p className="text-white font-medium">{participante.nome_responsavel}</p>
                          <p className="text-gray-500 text-sm">{participante.nome_conjuge}</p>
                        </div>
                      </td>
                      <td className="text-gray-400">{participante.email}</td>
                      <td className="text-gray-400">{participante.telefone}</td>
                      <td className="text-gray-400">{participante.cidade}</td>
                      <td>{getOrigemBadge(participante.origem)}</td>
                      <td>{getStatusBadge(participante.status_pagamento)}</td>
                      <td>{participante.status_pagamento === 'aprovado' && getCheckinBadge(participante.checkin_status)}</td>
                      <td>
                        {participante.codigo_ingresso ? (
                          <span className="font-mono text-kairos-orange text-sm">{participante.codigo_ingresso}</span>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetails(participante)}
                            className="text-gray-400 hover:text-white"
                            data-testid={`view-participante-${participante.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(participante)}
                            className="text-gray-400 hover:text-kairos-orange"
                            title="Editar dados"
                            data-testid={`edit-participante-${participante.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(participante)}
                            className="text-gray-400 hover:text-red-500"
                            data-testid={`delete-participante-${participante.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="bg-obsidian-200 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Detalhes do Participante</DialogTitle>
              <DialogDescription className="text-gray-400 text-sm">
                Informações da inscrição e gestão de status
              </DialogDescription>
            </DialogHeader>
            
            {selectedParticipante && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-kairos-orange/20 flex items-center justify-center">
                    <Users className="w-8 h-8 text-kairos-orange" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium text-lg">{selectedParticipante.nome_responsavel}</p>
                    <p className="text-gray-400">{selectedParticipante.nome_conjuge}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(selectedParticipante)}
                    className="border-kairos-orange/40 text-kairos-orange hover:bg-kairos-orange/10 hover:text-white"
                    data-testid="details-edit-participante-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar dados
                  </Button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-400">
                    <Mail className="w-5 h-5" />
                    <span>{selectedParticipante.email}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400">
                    <Phone className="w-5 h-5" />
                    <span>{selectedParticipante.telefone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400">
                    <MapPin className="w-5 h-5" />
                    <span>{selectedParticipante.cidade}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 p-4 bg-obsidian-50 rounded-lg">
                  <div>
                    <p className="text-gray-500 text-sm">Evento</p>
                    <p className="text-white">{getEventoName(selectedParticipante.evento_id)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Status</p>
                    {getStatusBadge(selectedParticipante.status_pagamento)}
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Código do Ingresso</p>
                    <p className="font-mono text-kairos-orange">
                      {selectedParticipante.codigo_ingresso || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Data de Inscrição</p>
                    <p className="text-white text-sm">
                      {formatDate(selectedParticipante.criado_em)}
                    </p>
                  </div>
                </div>

                {selectedParticipante.termo_aceito && (
                  <div className="p-4 bg-gray-500/10 border border-gray-500/30 rounded-lg">
                    <p className="text-gray-400 text-sm">
                      ✓ Termos aceitos em {formatDate(selectedParticipante.data_aceite)}
                    </p>
                    {selectedParticipante.ip_aceite && (
                      <p className="text-gray-500 text-xs mt-1">
                        IP: {selectedParticipante.ip_aceite}
                      </p>
                    )}
                  </div>
                )}

                {/* Status Management Section */}
                <div className="p-4 bg-obsidian-50 rounded-lg space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Edit className="w-4 h-4 text-kairos-orange" />
                    <p className="text-white font-medium">Alterar Status</p>
                  </div>
                  
                  {/* Payment Status */}
                  <div>
                    <label className="text-gray-400 text-sm block mb-2">Status do Pagamento</label>
                    <Select
                      value={selectedParticipante.status_pagamento}
                      onValueChange={(value) => handleUpdateStatus(
                        selectedParticipante.participante_id || selectedParticipante.id,
                        'pagamento',
                        value
                      )}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger className="bg-obsidian-200 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-obsidian-200 border-white/10">
                        <SelectItem value="pendente" className="text-yellow-400">Pendente</SelectItem>
                        <SelectItem value="aprovado" className="text-green-400">Aprovado</SelectItem>
                        <SelectItem value="rejeitado" className="text-red-400">Rejeitado</SelectItem>
                        <SelectItem value="reembolsado" className="text-blue-400">Reembolsado</SelectItem>
                        <SelectItem value="cancelado" className="text-gray-400">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Check-in Status */}
                  <div>
                    <label className="text-gray-400 text-sm block mb-2">Status do Check-in</label>
                    <Select
                      value={selectedParticipante.status_checkin || 'pendente'}
                      onValueChange={(value) => handleUpdateStatus(
                        selectedParticipante.participante_id || selectedParticipante.id,
                        'checkin',
                        value
                      )}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger className="bg-obsidian-200 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-obsidian-200 border-white/10">
                        <SelectItem value="pendente" className="text-yellow-400">Pendente</SelectItem>
                        <SelectItem value="realizado" className="text-green-400">Realizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {updatingStatus && (
                    <p className="text-gray-400 text-sm text-center">Atualizando...</p>
                  )}
                </div>

                {/* Delete Button */}
                <div className="pt-4 border-t border-white/10 space-y-2">
                  {selectedParticipante.status_pagamento === 'aprovado' && (
                    <Button
                      onClick={handleResendEmail}
                      disabled={resendingEmail}
                      className="w-full bg-kairos-orange hover:bg-kairos-orange/90 text-white"
                      data-testid="resend-confirmation-email-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {resendingEmail ? 'Enviando...' : 'Reenviar E-mail com QR Code'}
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setDetailsOpen(false);
                      openDeleteDialog(selectedParticipante);
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="delete-participante-detail-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir Participante
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-obsidian-200 border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Confirmar Exclusão</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                Tem certeza que deseja excluir o participante{' '}
                <span className="text-white font-medium">
                  {participanteToDelete?.nome_responsavel}
                </span>
                {participanteToDelete?.nome_conjuge && (
                  <span> e <span className="text-white font-medium">{participanteToDelete?.nome_conjuge}</span></span>
                )}
                ? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-transparent border-white/10 text-white hover:bg-white/5"
                disabled={deleting}
              >
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Excluindo...' : 'Excluir'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <EditParticipanteDialog
          participante={participanteToEdit}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={handleEdited}
        />
      </div>
    </AdminLayout>
  );
};

export default ParticipantesPage;
